import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../main/initializers/axios";
import { ApiConstants } from "../../../main/constants/ApiConstants";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import "./Reservations.css";
import "../HotelShared/HotelShared.css";
import BasicCli from "../../../main/components/basicCli/BasicCli";
import ReservationPackagesModal from "../ReservationPackagesModal/ReservationPackagesModal";
import HotelBillingModal from "../HotelBillingModal/HotelBillingModal";
import useGetUser from "../../../main/hooks/useGetUser";
import IUser from "../../../main/interfaces/IUser";

export interface IReservationViewModel {
  id: number;
  hotelId?: number;
  roomId: number;
  roomNumber?: string;
  roomStatus?: number;
  checkInDate: string;
  checkOutDate: string;
  status: number;
  guestName?: string;
  lockedPriceTotal?: number;
  depositAmount?: number;
  balanceDue?: number;
}

interface Props {
  reservations: IReservationViewModel[];
  hotelId?: number | null;
  embedded?: boolean;
  onRefresh?: () => void;
  hotelCheckInTime?: string;
  hotelCheckOutTime?: string;
}

const Reservations = ({
  reservations,
  hotelId,
  embedded = false,
  onRefresh,
  hotelCheckInTime,
  hotelCheckOutTime,
}: Props) => {
  const { i18n } = useTranslation();
  const currentUser = useGetUser() as IUser | null;
  const [selectedReservation, setSelectedReservation] = useState<IReservationViewModel | null>(null);
  const [billingReservation, setBillingReservation] = useState<IReservationViewModel | null>(null);
  const [isPrintingDailySummary, setIsPrintingDailySummary] = useState(false);
  const [isDailySummaryModalOpen, setIsDailySummaryModalOpen] = useState(false);
  const [users, setUsers] = useState<IUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [dailySummaryUserId, setDailySummaryUserId] = useState<string>("");
  const [dailySummaryDate, setDailySummaryDate] = useState(new Date().toISOString().slice(0, 10));

  const reservationStatuses: Record<number, string> = {
    1: i18n.t("reservationDesk.statusReserved"),
    2: i18n.t("reservationDesk.statusCheckedIn"),
    3: i18n.t("reservationDesk.statusCheckedOut"),
    4: i18n.t("reservationDesk.statusCancelled"),
    5: i18n.t("reservationDesk.statusNoShow"),
  };
  const roomStatuses: Record<number, string> = {
    1: i18n.t("room.available", { defaultValue: "Available" }),
    2: i18n.t("room.occupied", { defaultValue: "Occupied" }),
    3: i18n.t("room.cleaning", { defaultValue: "Cleaning" }),
    4: i18n.t("room.maintenance", { defaultValue: "Maintenance" }),
  };

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [],
  );

  const dailySummarySelectedUser = useMemo(
    () => users.find((user) => Number(user.id) === Number(dailySummaryUserId)),
    [dailySummaryUserId, users],
  );

  const getUserDisplayName = (user?: IUser | null) => {
    if (!user) return "-";

    const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
    if (fullName) return fullName;
    if (user.userName?.trim()) return user.userName.trim();
    if (user.email?.trim()) return user.email.trim();
    return `User #${user.id}`;
  };

  const loadUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const response = await axiosInstance.get(ApiConstants.User.users);
      const loadedUsers = Array.isArray(response.data) ? response.data.filter((user: IUser) => !user.isDelete) : [];
      setUsers(loadedUsers);
    } catch {
      toast.error(
        i18n.t("hotelBilling.messages.loadDailySummaryUsersError", {
          defaultValue: "Failed to load users for hotel daily summary.",
        }),
      );
    } finally {
      setIsLoadingUsers(false);
    }
  }, [i18n]);

  useEffect(() => {
    if (!isDailySummaryModalOpen || users.length > 0) {
      return;
    }

    void loadUsers();
  }, [isDailySummaryModalOpen, loadUsers, users.length]);

  useEffect(() => {
    if (!currentUser?.id || dailySummaryUserId) {
      return;
    }

    setDailySummaryUserId(String(currentUser.id));
  }, [currentUser?.id, dailySummaryUserId]);

  const formatReservationDateTime = (dateValue: string, hotelTime?: string) => {
    if (!dateValue) return "-";

    const datePart = dateValue.includes("T") ? dateValue.split("T")[0] : dateValue;
    const formattedDate = new Date(`${datePart}T00:00:00`).toLocaleDateString("en-CA");

    if (!hotelTime) {
      return formattedDate;
    }

    return `${formattedDate} ${hotelTime.slice(0, 5)}`;
  };

  const checkIn = async (id: number) => {
    try {
      await axiosInstance.post(ApiConstants.reservation.checkIn, {
        reservationId: id,
      });

      toast.success(i18n.t("reservationDesk.guestCheckedIn"));
      onRefresh?.();
    } catch {
      toast.error(i18n.t("reservationDesk.checkInFailed"));
    }
  };

  const checkOut = async (id: number) => {
    try {
      await axiosInstance.post(ApiConstants.reservation.checkOut, {
        reservationId: id,
      });

      toast.success(i18n.t("reservationDesk.guestCheckedOut"));
      onRefresh?.();
    } catch {
      toast.error(i18n.t("reservationDesk.checkOutFailed"));
    }
  };

  const cancelReservation = async (id: number) => {
    try {
      await axiosInstance.post(ApiConstants.reservation.cancel, {
        reservationId: id,
      });

      toast.success(i18n.t("reservationDesk.reservationCancelled"));
      onRefresh?.();
    } catch {
      toast.error(i18n.t("reservationDesk.cancelFailed"));
    }
  };

  const openDailySummaryModal = () => {
    if (!hotelId) {
      toast.error(i18n.t("hotelBilling.messages.printDailySummaryMissingHotel"));
      return;
    }

    if (!dailySummaryUserId && currentUser?.id) {
      setDailySummaryUserId(String(currentUser.id));
    }

    setDailySummaryDate(new Date().toISOString().slice(0, 10));
    setIsDailySummaryModalOpen(true);
  };

  const closeDailySummaryModal = () => {
    if (isPrintingDailySummary) {
      return;
    }

    setIsDailySummaryModalOpen(false);
  };

  const printHotelDailySummary = async () => {
    if (!hotelId) {
      toast.error(i18n.t("hotelBilling.messages.printDailySummaryMissingHotel"));
      return;
    }

    if (!dailySummaryUserId) {
      toast.error(
        i18n.t("hotelBilling.messages.printDailySummaryUserRequired", {
          defaultValue: "User is required.",
        }),
      );
      return;
    }

    if (!dailySummaryDate) {
      toast.error(
        i18n.t("hotelBilling.messages.printDailySummaryDateRequired", {
          defaultValue: "Date is required.",
        }),
      );
      return;
    }

    setIsPrintingDailySummary(true);
    try {
      const response = await axiosInstance.post(ApiConstants.hotelFolio.printDailySummary, {
        hotelId,
        userId: Number(dailySummaryUserId),
        user: Number(dailySummaryUserId),
        date: `${dailySummaryDate}T00:00:00`,
      });

      if (Boolean(response.data)) {
        toast.success(i18n.t("hotelBilling.messages.printDailySummaryQueued"));
        setIsDailySummaryModalOpen(false);
      } else {
        toast.error(i18n.t("hotelBilling.messages.printDailySummaryNoPrinter"));
      }
    } catch {
      toast.error(i18n.t("hotelBilling.messages.printDailySummaryError"));
    } finally {
      setIsPrintingDailySummary(false);
    }
  };

  const content = (
    <div
      className={
        embedded
          ? "hotel-reservations-container hotel-reservations-embedded"
          : "hotelSectionPage hotel-reservations-container"
      }
    >
      <div
        className={
          embedded
            ? "ui-panel hotelSectionHeader hotel-reservations-header hotel-reservations-header-embedded"
            : "ui-panel hotelSectionHeader hotel-reservations-header"
        }
        >
        <div className="hotelSectionHeading">
          <h2 className="hotel-reservations-title">{i18n.t("reservationDesk.reservationsTitle")}</h2>
        </div>
        <div className="hotelSectionActions">
          <button
            type="button"
            className="hotel-action-btn hotel-secondary-btn"
            onClick={openDailySummaryModal}
            disabled={!hotelId || isPrintingDailySummary}
          >
            {isPrintingDailySummary
              ? i18n.t("hotelBilling.actions.printingDailySummary")
              : i18n.t("hotelBilling.actions.printDailySummary")}
          </button>
        </div>
      </div>

      <div
        className={
          embedded
            ? "bcCard hotelSectionCard hotelTableWrap hotel-reservations-table-card hotel-reservations-table-card-embedded"
            : "bcCard hotelSectionCard hotelTableWrap hotel-reservations-table-card"
        }
      >
        <table className="hotelDataTable hotel-table">
          <thead>
            <tr>
              <th>{i18n.t("reservationDesk.room")}</th>
              <th>{i18n.t("reservationDesk.guest")}</th>
              <th>{i18n.t("reservationDesk.checkIn")}</th>
              <th>{i18n.t("reservationDesk.checkOut")}</th>
              <th>{i18n.t("generic.status")}</th>
              <th>{i18n.t("generic.total")}</th>
              <th>{i18n.t("reservationDesk.balanceDue")}</th>
              <th>{i18n.t("reservationDesk.actions")}</th>
            </tr>
          </thead>

          <tbody>
            {reservations.map((reservation) => (
              <tr key={reservation.id}>
                <td>
                  <div className="hotelReservationRoomCell">
                    <strong>{reservation.roomNumber ?? `#${reservation.roomId}`}</strong>
                    {reservation.roomStatus ? (
                      <span className={`hotelRoomStatusBadge room-status-${reservation.roomStatus}`}>
                        {roomStatuses[reservation.roomStatus] ?? `#${reservation.roomStatus}`}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td>{reservation.guestName || "-"}</td>
                <td>{formatReservationDateTime(reservation.checkInDate, hotelCheckInTime)}</td>
                <td>{formatReservationDateTime(reservation.checkOutDate, hotelCheckOutTime)}</td>
                <td>
                  <span className={`hotelReservationStatus status-${reservation.status}`}>
                    {reservationStatuses[reservation.status] ?? `#${reservation.status}`}
                  </span>
                </td>
                <td>{formatter.format(reservation.lockedPriceTotal ?? 0)}</td>
                <td>{formatter.format(reservation.balanceDue ?? 0)}</td>
                <td className="hotel-reservations-actions-cell">
                  <div className="hotelActionRow hotelActionRowWrap">
                    {reservation.status !== 4 && (
                      <button
                        className="hotel-action-btn hotel-secondary-btn"
                        onClick={() => setSelectedReservation(reservation)}
                      >
                        {i18n.t("reservationDesk.managePackages")}
                      </button>
                    )}

                    {(reservation.status === 1 || reservation.status === 2) && (
                      <button
                        className="hotel-action-btn hotel-billing-btn"
                        onClick={() => setBillingReservation(reservation)}
                      >
                        Hotel Billing
                      </button>
                    )}

                    {reservation.status === 1 && (
                      <>
                        <button
                          className="hotel-action-btn hotel-checkin-btn"
                          onClick={() => checkIn(reservation.id)}
                        >
                          {i18n.t("reservationDesk.checkIn")}
                        </button>

                        <button
                          className="hotel-action-btn hotel-cancel-btn"
                          onClick={() => cancelReservation(reservation.id)}
                        >
                          {i18n.t("generic.cancel")}
                        </button>
                      </>
                    )}

                    {reservation.status === 2 && (
                      <button
                        className="hotel-action-btn hotel-checkout-btn"
                        onClick={() => checkOut(reservation.id)}
                      >
                        {i18n.t("reservationDesk.checkOut")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {reservations.length === 0 && (
              <tr>
                <td colSpan={8} className="hotelEmptyState">
                  {i18n.t("reservationDesk.noReservations")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedReservation && (
        <ReservationPackagesModal
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
          onSaved={() => {
            onRefresh?.();
          }}
        />
      )}

      {billingReservation && (
        <HotelBillingModal
          reservation={billingReservation}
          onClose={() => setBillingReservation(null)}
          onSaved={() => {
            onRefresh?.();
          }}
        />
      )}

      {isDailySummaryModalOpen && (
        <div className="reservation-modal reservation-packages-overlay" onClick={closeDailySummaryModal}>
          <div
            className="reservation-modal-content reservation-packages-modal hotel-daily-summary-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="reservation-modal-top">
              <div>
                <span className="reservation-kicker">
                  {i18n.t("hotelBilling.actions.printDailySummary")}
                </span>
                <h3>
                  {i18n.t("hotelBilling.dailySummaryModal.title", {
                    defaultValue: "Hotel daily closing",
                  })}
                </h3>
                <p>
                  {i18n.t("hotelBilling.dailySummaryModal.subtitle", {
                    defaultValue: "Select the user and date before printing the hotel daily summary.",
                  })}
                </p>
              </div>
            </div>

            <div className="reservation-summary hotel-daily-summary-preview">
              <div className="reservation-summary-item">
                <span>{i18n.t("hotelBilling.dailySummaryModal.user", { defaultValue: "User" })}</span>
                <strong>{dailySummarySelectedUser ? getUserDisplayName(dailySummarySelectedUser) : "-"}</strong>
              </div>
              <div className="reservation-summary-item">
                <span>{i18n.t("generic.date")}</span>
                <strong>{dailySummaryDate || "-"}</strong>
              </div>
            </div>

            <div className="reservation-package-form-grid hotel-daily-summary-grid">
              <div className="modal-field">
                <label>
                  {i18n.t("hotelBilling.dailySummaryModal.user", { defaultValue: "User" })} *
                </label>
                <select
                  value={dailySummaryUserId}
                  onChange={(event) => setDailySummaryUserId(event.target.value)}
                  disabled={isLoadingUsers || isPrintingDailySummary}
                >
                  <option value="">
                    {isLoadingUsers
                      ? i18n.t("hotelBilling.dailySummaryModal.loadingUsers", {
                          defaultValue: "Loading users...",
                        })
                      : i18n.t("hotelBilling.dailySummaryModal.selectUser", {
                          defaultValue: "Select user",
                        })}
                  </option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {getUserDisplayName(user)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-field">
                <label>{i18n.t("generic.date")} *</label>
                <input
                  type="date"
                  value={dailySummaryDate}
                  onChange={(event) => setDailySummaryDate(event.target.value)}
                  disabled={isPrintingDailySummary}
                />
              </div>
            </div>

            <p className="reservation-package-helper hotel-daily-summary-helper">
              {i18n.t("hotelBilling.dailySummaryModal.requiredHint", {
                defaultValue: "User and date are required.",
              })}
            </p>

            <div className="modal-actions">
              <button
                type="button"
                className="hotel-action-btn hotel-secondary-btn"
                onClick={closeDailySummaryModal}
                disabled={isPrintingDailySummary}
              >
                {i18n.t("generic.close")}
              </button>
              <button
                type="button"
                className="hotel-action-btn hotel-checkout-btn"
                onClick={() => void printHotelDailySummary()}
                disabled={isPrintingDailySummary || !dailySummaryUserId || !dailySummaryDate}
              >
                {isPrintingDailySummary
                  ? i18n.t("hotelBilling.actions.printingDailySummary")
                  : i18n.t("hotelBilling.actions.printDailySummary")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return <BasicCli>{content}</BasicCli>;
};

export default Reservations;
